// src/stores/adminStore.ts
import { proxy, subscribe } from 'valtio';
import { debounce } from 'lodash';
import type {
  AdminStore,
  Location,
  Owner,
  SampleAsset,
} from '../types/admin';
import { api } from '../utils/api';
import { globalConfigStore } from './globalConfigStore';

// Default data - matches current sampleDataGenerator.ts
const defaultData: AdminStore = {
  locations: [
    { id: '1', name: 'Head Office - Server Room', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'Head Office - Finance Department', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'Head Office - HR Department', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Head Office - Reception', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '5', name: 'Data Centre - Primary', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '6', name: 'Data Centre - Secondary', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '7', name: 'Branch Office - Manchester', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '8', name: 'Branch Office - Birmingham', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '9', name: 'Branch Office - Leeds', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '10', name: 'Remote Workers', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '11', name: 'Cloud Infrastructure', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '12', name: 'Building Perimeter & Entrances', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  owners: [
    { id: '1', name: 'IT Department', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'Finance Department', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'HR Department', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Operations', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '5', name: 'Security Team', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '6', name: 'Facilities Management', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '7', name: 'Legal & Compliance', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '8', name: 'Executive Team', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  sampleAssets: [
    { id: '1', name: 'Dell PowerEdge R750 Server', sku: 'SRV-DELL-R750-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'HP ProLiant DL380 Server', sku: 'SRV-HP-DL380-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'Cisco Catalyst 9300 Switch', sku: 'NET-CISCO-9300-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Fortinet FortiGate 100F Firewall', sku: 'SEC-FORTI-100F-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '5', name: 'APC Smart-UPS 3000VA', sku: 'PWR-APC-3000-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '6', name: 'Dell Latitude 5540 Laptop', sku: 'LAP-DELL-5540-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '7', name: 'HP EliteBook 850 G10', sku: 'LAP-HP-850G10-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '8', name: 'Synology DS1821+ NAS', sku: 'STR-SYN-1821-001', assetCategory: 'IT Assets', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '9', name: 'Microsoft 365 E5 Licence', sku: 'SW-MS365-E5-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '10', name: 'Adobe Creative Cloud', sku: 'SW-ADOBE-CC-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '11', name: 'Veeam Backup & Replication', sku: 'SW-VEEAM-BR-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '12', name: 'CrowdStrike Falcon', sku: 'SW-CROWD-FAL-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '13', name: 'ServiceNow ITSM', sku: 'SW-SNOW-ITSM-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '14', name: 'Salesforce CRM', sku: 'SW-SFDC-CRM-001', assetCategory: 'Software', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '15', name: 'Customer Database', sku: 'DATA-CUST-DB-001', assetCategory: 'Data', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '16', name: 'Financial Records Archive', sku: 'DATA-FIN-ARC-001', assetCategory: 'Data', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '17', name: 'Employee HR Records', sku: 'DATA-HR-REC-001', assetCategory: 'Data', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '18', name: 'Project Documentation Repository', sku: 'DATA-PROJ-DOC-001', assetCategory: 'Data', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '19', name: 'Backup Tape Archive', sku: 'DATA-TAPE-ARC-001', assetCategory: 'Data', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '20', name: 'Axis P1435-LE Security Camera', sku: 'SEC-AXIS-P1435-001', assetCategory: 'Physical Security', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '21', name: 'HID Global Access Control System', sku: 'SEC-HID-ACS-001', assetCategory: 'Physical Security', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '22', name: 'Chubb Fire Safe', sku: 'SEC-CHUBB-SAFE-001', assetCategory: 'Physical Security', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '23', name: 'Intruder Alarm System', sku: 'SEC-ALARM-SYS-001', assetCategory: 'Physical Security', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '24', name: 'Cisco ISR 4451 Router', sku: 'NET-CISCO-4451-001', assetCategory: 'Network Infrastructure', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '25', name: 'Aruba AP-535 Wireless Access Point', sku: 'NET-ARUBA-535-001', assetCategory: 'Network Infrastructure', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '26', name: 'Palo Alto PA-850 Firewall', sku: 'NET-PALO-850-001', assetCategory: 'Network Infrastructure', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '27', name: 'F5 BIG-IP Load Balancer', sku: 'NET-F5-BIGIP-001', assetCategory: 'Network Infrastructure', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '28', name: 'AWS Production Environment', sku: 'CLD-AWS-PROD-001', assetCategory: 'Cloud Services', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '29', name: 'Azure AD Premium P2', sku: 'CLD-AZR-AAD-001', assetCategory: 'Cloud Services', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '30', name: 'Google Workspace Enterprise', sku: 'CLD-GWS-ENT-001', assetCategory: 'Cloud Services', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '31', name: 'Cloudflare Enterprise', sku: 'CLD-CF-ENT-001', assetCategory: 'Cloud Services', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '32', name: 'Information Security Policy', sku: 'DOC-ISP-001', assetCategory: 'Documentation', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '33', name: 'Business Continuity Plan', sku: 'DOC-BCP-001', assetCategory: 'Documentation', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '34', name: 'Disaster Recovery Plan', sku: 'DOC-DRP-001', assetCategory: 'Documentation', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '35', name: 'Acceptable Use Policy', sku: 'DOC-AUP-001', assetCategory: 'Documentation', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '36', name: 'Security Awareness Training Programme', sku: 'PER-SAT-001', assetCategory: 'Personnel', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '37', name: 'IT Support Team', sku: 'PER-ITS-001', assetCategory: 'Personnel', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '38', name: 'Security Operations Centre', sku: 'PER-SOC-001', assetCategory: 'Personnel', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  likelihoodScale: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
  impactScale: ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'],
  controlStatusOptions: ['Not Started', 'In Progress', 'Implemented'],
  reviewPeriodOptions: [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' },
  ],

  // Non-InfoSec Risk Configuration
  nonInfoSecRiskCategories: [
    { id: '1', name: 'Operational Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'Financial Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'Strategic Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Compliance Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '5', name: 'Reputational Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '6', name: 'Health & Safety', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '7', name: 'Environmental Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '8', name: 'Supply Chain Risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  nonInfoSecMitigations: [
    { id: '1', name: 'Process Improvement', description: 'Improve existing processes to reduce risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'Training & Awareness', description: 'Educate staff to prevent incidents', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'Monitoring & Detection', description: 'Implement monitoring systems', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Redundancy & Backup', description: 'Add backup systems or redundancy', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '5', name: 'Insurance', description: 'Transfer risk through insurance coverage', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '6', name: 'Contractual Controls', description: 'Use contracts to manage third-party risks', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '7', name: 'Physical Security', description: 'Enhance physical security measures', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '8', name: 'Custom Mitigation', description: 'Define a custom mitigation approach', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  nonInfoSecTreatments: [
    { id: '1', name: 'Treat the risk', detail: 'Implement controls to reduce likelihood or severity', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '2', name: 'Avoid the risk', detail: 'Stop the activity that causes the risk', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '3', name: 'Transfer the risk', detail: 'Transfer risk to third party (e.g., insurance)', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { id: '4', name: 'Accept the risk', detail: 'Accept the risk as it is within tolerance', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  ],

  nonInfoSecLikelihoodScale: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
  nonInfoSecSeverityScale: ['Insignificant', 'Minor', 'Moderate', 'Serious', 'Catastrophic'],
  nonInfoSecControlStatusOptions: ['Not Started', 'In Progress', 'Under Review', 'Implemented', 'Not Applicable'],

  aiAssistEnabled: false,

  // Display preferences — date format applies to all date display across the app.
  // Uses date-fns format tokens. See client/src/utils/dateUtils.ts for the
  // presets surfaced in the Admin > System Configuration > Date Format tab.
  dateFormat: 'dd/MM/yyyy',

  // Sign the user out after this many minutes of inactivity. 0 disables it.
  idleTimeoutMinutes: 60,
};

// Initialise with defaults - loadFromAPI will overwrite when data arrives
export const adminStore = proxy<AdminStore>({ ...defaultData });

// Track last-known server timestamp outside reactive proxy to avoid save loops
let adminLastSavedAt = '';

// Guard: suppress auto-saves while loading from API to prevent writing defaults/stale data back
let adminLoading = false;

// Debounced save to API with conflict detection
const debouncedSaveAdmin = debounce(async () => {
  if (adminLoading) return; // Don't save while loading — store may contain defaults
  try {
    const payload = { ...JSON.parse(JSON.stringify(adminStore)), _updatedAt: adminLastSavedAt };
    const response = await api.put('/admin', payload) as Record<string, unknown>;
    if (response._updatedAt !== undefined) {
      adminLastSavedAt = (response._updatedAt as string) || '';
    }
  } catch (e: unknown) {
    const err = e as Error & { status?: number };
    if (err.status === 409) {
      console.warn('Admin config conflict detected, reloading...');
      await loadAdminFromAPI();
    } else {
      console.error('Failed to save admin config to API:', e);
    }
  }
}, 500);

// Load admin config from API (called on app init)
export const loadAdminFromAPI = async () => {
  adminLoading = true;
  let needsFirstTimeSave = false;
  try {
    const data = await api.get('/admin') as Record<string, unknown> | null;
    if (data) {
      // Extract _updatedAt before assigning to store (use !== undefined so '' is handled)
      if (data._updatedAt !== undefined) {
        adminLastSavedAt = (data._updatedAt as string) || '';
        delete data._updatedAt;
      }
      Object.assign(adminStore, data);
      return;
    }
    // No data from API — try localStorage
    const stored = localStorage.getItem('admin-config-data');
    if (stored) {
      Object.assign(adminStore, JSON.parse(stored));
      return;
    }
    // No data anywhere — defaults are already loaded, save them for first time
    needsFirstTimeSave = true;
  } catch (e) {
    console.error('Failed to load admin config from API:', e);
    // Fallback to localStorage
    try {
      const stored = localStorage.getItem('admin-config-data');
      if (stored) {
        Object.assign(adminStore, JSON.parse(stored));
        return;
      }
    } catch {}
  } finally {
    // Valtio delivers the subscribe callback for the Object.assign above on
    // a later microtask, so it lands AFTER this block clears the flag and
    // schedules a save of the data we just loaded. Wait a macrotask so the
    // notification has fired, then drop that spurious pending save.
    await new Promise((resolve) => setTimeout(resolve, 0));
    debouncedSaveAdmin.cancel();
    adminLoading = false;
  }
  if (needsFirstTimeSave) {
    debouncedSaveAdmin();
  }
};

// Auto-save on mutations (debounced)
subscribe(adminStore, debouncedSaveAdmin);

// Helper to generate unique IDs
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const adminActions = {
  // Locations
  addLocation: (name: string) => {
    const now = new Date().toISOString();
    adminStore.locations.push({
      id: generateId(),
      name,
      createdAt: now,
      updatedAt: now,
    });
  },

  updateLocation: (id: string, name: string) => {
    const idx = adminStore.locations.findIndex(l => l.id === id);
    if (idx !== -1) {
      adminStore.locations[idx] = {
        ...adminStore.locations[idx],
        name,
        updatedAt: new Date().toISOString(),
      };
    }
  },

  deleteLocation: (id: string) => {
    adminStore.locations = adminStore.locations.filter(l => l.id !== id);
  },

  // Owners
  addOwner: (name: string, department?: string) => {
    const now = new Date().toISOString();
    adminStore.owners.push({
      id: generateId(),
      name,
      department,
      createdAt: now,
      updatedAt: now,
    });
  },

  updateOwner: (id: string, updates: Partial<Owner>) => {
    const idx = adminStore.owners.findIndex(o => o.id === id);
    if (idx !== -1) {
      adminStore.owners[idx] = {
        ...adminStore.owners[idx],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    }
  },

  deleteOwner: (id: string) => {
    adminStore.owners = adminStore.owners.filter(o => o.id !== id);
  },

  // Sample Assets
  addSampleAsset: (name: string, sku: string, assetCategory: string) => {
    const now = new Date().toISOString();
    adminStore.sampleAssets.push({
      id: generateId(),
      name,
      sku,
      assetCategory,
      createdAt: now,
      updatedAt: now,
    });
  },

  updateSampleAsset: (id: string, updates: Partial<SampleAsset>) => {
    const idx = adminStore.sampleAssets.findIndex(a => a.id === id);
    if (idx !== -1) {
      adminStore.sampleAssets[idx] = {
        ...adminStore.sampleAssets[idx],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    }
  },

  deleteSampleAsset: (id: string) => {
    adminStore.sampleAssets = adminStore.sampleAssets.filter(a => a.id !== id);
  },

  // Reset to defaults
  resetToDefaults: () => {
    Object.assign(adminStore, defaultData);
  },

  // Export/Import
  exportConfig: (): string => {
    return JSON.stringify(adminStore, null, 2);
  },

  importConfig: (jsonString: string): { success: boolean; error?: string } => {
    try {
      const data = JSON.parse(jsonString);
      Object.assign(adminStore, data);
      return { success: true };
    } catch (e) {
      return { success: false, error: 'Invalid JSON format' };
    }
  },
};

// Helper functions for other modules to get data
export const getInfoSecCategories = () => globalConfigStore.infoSecCategories;
export const getLocations = () => adminStore.locations.map(l => l.name);
export const getOwners = () => adminStore.owners.map(o => o.name);
export const getThreatsByCategory = (category: string) => {
  // Threat types store ISO 27001 category names directly in their assetCategories array
  return globalConfigStore.threatTypes
    .filter(t => t.assetCategories.includes(category))
    .map(t => t.name);
};
export const getVulnerabilitiesByThreat = (threat: string) =>
  globalConfigStore.vulnerabilities.filter(v => v.threatTypes.includes(threat)).map(v => v.name);
export const getRiskCategories = () => globalConfigStore.riskCategories.map(r => r.name);
export const getTreatmentStrategies = () => globalConfigStore.treatmentStrategies.map(t => t.name);
export const getTreatmentStrategiesWithDescriptions = () =>
  globalConfigStore.treatmentStrategies.map(t => ({ name: t.name, description: t.description }));
export const getTreatmentDescription = (strategyName: string) =>
  globalConfigStore.treatmentStrategies.find(t => t.name === strategyName)?.description || '';
export const getStandardWordingByCategory = (category: 'implementation' | 'justification') =>
  globalConfigStore.standardWording.filter(w => w.category === category).map(w => ({ id: w.id, name: w.name, text: w.text, controls: w.controls }));

// Helper to extract control number (e.g., "5.1" from "5.1 Policies for information security")
const getControlNumber = (controlName: string): string => {
  return controlName?.split(' ')[0] || '';
};

// Helper to parse control number for sorting (e.g., "5.1" -> 5.1, "5.10" -> 5.10)
const parseControlNumber = (controlNum: string): number => {
  const parts = controlNum.split('.');
  if (parts.length === 2) {
    return parseFloat(parts[0]) * 100 + parseFloat(parts[1]);
  }
  return parseFloat(controlNum) || 999;
};

// Helper to sort wording: specific controls first (by number), then applies-to-all
const sortWording = (wording: Array<{ id: string; name: string; text: string; controls: string[] }>) => {
  return wording.sort((a, b) => {
    const aHasControls = a.controls && a.controls.length > 0;
    const bHasControls = b.controls && b.controls.length > 0;

    // Specific controls come first
    if (aHasControls && !bHasControls) return -1;
    if (!aHasControls && bHasControls) return 1;

    // If both have controls, sort by first control number
    if (aHasControls && bHasControls) {
      const aNum = parseControlNumber(getControlNumber(a.controls[0]));
      const bNum = parseControlNumber(getControlNumber(b.controls[0]));
      return aNum - bNum;
    }

    // Both apply to all - keep original order
    return 0;
  });
};

export const getImplementationWording = (controlName?: string) => {
  const wording = globalConfigStore.standardWording.filter(w => w.category === 'implementation');
  if (controlName && controlName.trim() !== '') {
    const selectedControlNumber = getControlNumber(controlName);
    // Return wording that either has no controls (applies to all) or matches by control number
    const filtered = wording.filter(w => {
      if (!w.controls || w.controls.length === 0) return true;
      return w.controls.some(c => {
        const wordingControlNumber = getControlNumber(c);
        return c === controlName || wordingControlNumber === selectedControlNumber;
      });
    }).map(w => ({ id: w.id, name: w.name, text: w.text, controls: w.controls || [] }));
    return sortWording(filtered);
  }
  return sortWording(wording.map(w => ({ id: w.id, name: w.name, text: w.text, controls: w.controls || [] })));
};

export const getJustificationWording = (controlName?: string) => {
  const wording = globalConfigStore.standardWording.filter(w => w.category === 'justification');
  if (controlName && controlName.trim() !== '') {
    const selectedControlNumber = getControlNumber(controlName);
    // Return wording that either has no controls (applies to all) or matches by control number
    const filtered = wording.filter(w => {
      if (!w.controls || w.controls.length === 0) return true;
      return w.controls.some(c => {
        const wordingControlNumber = getControlNumber(c);
        return c === controlName || wordingControlNumber === selectedControlNumber;
      });
    }).map(w => ({ id: w.id, name: w.name, text: w.text, controls: w.controls || [] }));
    return sortWording(filtered);
  }
  return sortWording(wording.map(w => ({ id: w.id, name: w.name, text: w.text, controls: w.controls || [] })));
};

export const getSampleAssetsByCategory = (category: string) =>
  adminStore.sampleAssets.filter(a => a.assetCategory === category);
export const getInfoSecControls = () => globalConfigStore.infoSecControls.map(c => c.name);
